import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import DeleteIcon from '@mui/icons-material/Delete';
import SyncIcon from '@mui/icons-material/Sync';
import {
  Box,
  Button,
  CircularProgress,
  Container,
  Grid,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useQueryClient } from '@tanstack/react-query';

import { makeAuthenticatedApiCall } from '../apiHelper';
import { useCofinancementCounts, useCofinancementList } from '../hooks/useApiQueries';
import { useAuthRedirect } from '../hooks/useAuthRedirect';
import theme from '../theme';
import { useAuth } from './AuthContext';

// Interfaces pour les données de cofinancement
interface CofinancementData {
  id: number;
  deal_id: number;
  estimated_selling_price: number;
  client_desired_price?: number;
  estimated_rotation_weeks: number;
  encheres_vo_price?: number;
  purchase_price?: number;
  status: 'pending' | 'client_validated' | 'skipcar_validated' | 'rejected';
  client_validated: boolean;
  skipcar_validated: boolean;
  validated_at?: string;
  skipcar_validated_at?: string;
  created_at: string;
  updated_at: string;
  registration_plate?: string;
  make?: string;
  model?: string;
  org_name?: string;
  kms?: number;
  reg_date?: string;
}

const stickyCellStyle = {
  background: '#262626',
  fontWeight: 'bold',
  minWidth: 50,
  color: 'white',
  position: 'sticky',
  top: 0,
  zIndex: 100,
};

const SortIcon = styled('span')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginLeft: theme.spacing(1),
  color: 'white',
}));

const mapSortColumnToQuery = (column: string): string => {
  const columnMap: { [key: string]: string } = {
    id: 'id',
    created_at: 'created_at',
    updated_at: 'updated_at',
    status: 'status',
    registration_plate: 'registration_plate',
    estimated_rotation_weeks: 'estimated_rotation_weeks',
  };
  return columnMap[column] || 'id';
};

const DELETED_TAB_INDEX = 4;

const CofinancementList = () => {
  const { currentUser } = useAuth();
  const { redirectToLogin } = useAuthRedirect();
  const userRole = currentUser?.data?.data?.role;

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isTableLoading, setIsTableLoading] = useState<boolean>(true);
  const [isFullSyncLoading, setIsFullSyncLoading] = useState<boolean>(false);
  const [selectedTab, setSelectedTab] = useState<number>(1); // Par défaut sur "En attente"
  const [sortColumn, setSortColumn] = useState<string>('id');
  const [sortOrder, setSortOrder] = useState<string>('DESC');
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(25);
  const [isDeleting, setIsDeleting] = useState(false);

  const [searchQuery, setSearchQuery] = useState<string>('');
  const [hasSearched, setHasSearched] = useState<boolean>(false);

  const handleSearch = (query: string) => {
    setSearchQuery(query);
    setHasSearched(true);
    setPage(0);
  };

  const resetSearch = () => {
    setSearchQuery('');
    setHasSearched(false);
    setPage(0);
  };
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Vérifier l'authentification et rediriger si nécessaire
  useEffect(() => {
    if (!currentUser) {
      redirectToLogin();
    }
  }, [currentUser, redirectToLogin]);

  // Vérifier que l'utilisateur a les droits
  if (userRole !== 'arbitrator' && userRole !== 'god') {
    return (
      <Container maxWidth={false}>
        <Typography variant="h6" sx={{ mt: 4, textAlign: 'center', color: 'error.main' }}>
          Accès non autorisé. Cette page est réservée aux arbitres et administrateurs.
        </Typography>
      </Container>
    );
  }

  // Déterminer le statut selon l'onglet sélectionné
  const getStatusFromTab = (tabIndex: number): string | undefined => {
    switch (tabIndex) {
      case 0:
        return undefined; // Toutes
      case 1:
        return 'pending'; // En attente
      case 2:
        return 'client_validated'; // Validé par le client
      case 3:
        return 'skipcar_validated'; // Validés
      default:
        return undefined;
    }
  };

  const status = searchQuery ? undefined : getStatusFromTab(selectedTab);

  // Fonction pour supprimer un cofinancement
  const handleDeleteCofinancement = async (id: number) => {
    const confirmDelete = window.confirm('Êtes-vous sûr de vouloir supprimer cette demande Skipcar+ ?');
    if (confirmDelete) {
      setIsDeleting(true);
      try {
        await makeAuthenticatedApiCall('delete', `/api/cofinancement/${id}`);

        // Invalider agressivement toutes les queries cofinancement pour mettre à jour immédiatement tous les onglets
        console.log('🗑️ Invalidation complète du cache cofinancement après suppression...');
        await queryClient.invalidateQueries({ queryKey: ['cofinancementList'] });
        await queryClient.refetchQueries({ queryKey: ['cofinancementList'] });

        alert('La demande Skipcar+ a été supprimée avec succès.');
      } catch (error) {
        console.error('Erreur lors de la suppression de la demande de cofinancement:', error);
        alert('Une erreur est survenue lors de la suppression de la demande Skipcar+.');
      } finally {
        setIsDeleting(false);
      }
    }
  };

  // Utiliser les hooks useApiQueries
  const {
    data: cofinancementListData,
    isLoading: isCofinancementLoading,
    refetch: refetchCofinancementList,
  } = useCofinancementList({
    page: page + 1,
    rowsPerPage,
    searchQuery,
    status,
    sortColumn: mapSortColumnToQuery(sortColumn),
    sortOrder,
    queryNonVisible: selectedTab === DELETED_TAB_INDEX,
  });

  const { data: cofinancementCountsData, refetch: refetchCofinancementCounts } = useCofinancementCounts();

  const cofinancementData = (cofinancementListData as any)?.data || [];
  const totalItems = (cofinancementListData as any)?.total || 0;
  const cofinancementCounts = (cofinancementCountsData as any) || {
    pending: 0,
    client_validated: 0,
    skipcar_validated: 0,
    rejected: 0,
  };

  // Calculer le total global de tous les cofinancements
  const totalGlobalCofinancements =
    cofinancementCounts.pending +
    cofinancementCounts.client_validated +
    cofinancementCounts.skipcar_validated +
    cofinancementCounts.rejected;

  const getStatusLabel = (status: string) => {
    const statusMap: { [key: string]: string } = {
      pending: 'En attente',
      client_validated: 'Validé par le client',
      skipcar_validated: 'Validé par SKIPCAR',
      rejected: 'Rejeté',
    };
    return statusMap[status] || status;
  };

  const getStatusStyle = (status: string) => {
    switch (status) {
      case 'pending':
        return {
          backgroundColor: '#FF6060',
          color: 'white',
          opacity: 1,
          padding: '4px 8px',
          borderRadius: '4px',
          fontWeight: 600,
          display: 'inline-block',
        };
      case 'client_validated':
        return {
          backgroundColor: '#FFA500',
          color: 'white',
          opacity: 1,
          padding: '4px 8px',
          borderRadius: '4px',
          fontWeight: 600,
          display: 'inline-block',
        };
      case 'skipcar_validated':
        return {
          color: '#008000',
          fontWeight: 'bold',
          fontSize: '16px',
        };
      case 'rejected':
        return {
          backgroundColor: '#FF0000',
          color: 'white',
          opacity: 1,
          padding: '4px 8px',
          borderRadius: '4px',
          fontWeight: 600,
          display: 'inline-block',
        };
      default:
        return {
          color: 'inherit',
          fontWeight: 'normal',
        };
    }
  };

  // Mise à jour de l'état de chargement basé sur les hooks
  useEffect(() => {
    setIsTableLoading(isCofinancementLoading);
    setIsLoading(isCofinancementLoading);
  }, [isCofinancementLoading]);

  useEffect(() => {
    if (searchQuery !== '') {
      setPage(0);
    }
  }, [searchQuery]);

  const handleChangePage = (_event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSort = (column: string) => {
    const isAsc = sortColumn === column && sortOrder === 'ASC';
    setSortOrder(isAsc ? 'DESC' : 'ASC');
    setSortColumn(column);
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue);
    setPage(0);
  };

  const onRefresh = async (forceFullSync = false) => {
    if (forceFullSync) {
      setIsFullSyncLoading(true);
    }
    setIsTableLoading(true);

    try {
      // Invalider le cache
      console.log('🗑️ Invalidation du cache React Query...');

      if (forceFullSync) {
        console.log('🔄 Synchronisation complète : invalidation de tout le cache...');
        await queryClient.invalidateQueries();
        await queryClient.refetchQueries();
      } else {
        await queryClient.invalidateQueries({ queryKey: ['cofinancementList'] });
        await queryClient.invalidateQueries({ queryKey: ['cofinancementCounts'] });
      }

      // Rafraîchir les données
      await refetchCofinancementList();
      await refetchCofinancementCounts();

      console.log(`🔄 Actualisation des données cofinancement terminée${forceFullSync ? ' (COMPLÈTE)' : ''}`);
    } catch (error) {
      console.error("Erreur lors de l'actualisation:", error);
    } finally {
      setIsTableLoading(false);
      if (forceFullSync) {
        setIsFullSyncLoading(false);
      }
    }
  };

  const handleCofinancementClick = (dealId: number) => {
    navigate(`/skipcar-plus/${dealId}/simulation`);
  };

  const formatPrice = (price: number | undefined) => {
    if (!price) return 'N/A';
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
      maximumFractionDigits: 0,
    }).format(price);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR');
  };

  const formatKilometers = (kms: number | undefined) => {
    if (!kms) return 'N/A';
    return new Intl.NumberFormat('fr-FR').format(kms);
  };

  const formatMEC = (mecDate: string | undefined) => {
    if (!mecDate) return 'N/A';
    return new Date(mecDate).toLocaleDateString('fr-FR');
  };

  // N'afficher le message "Pas encore de dossier Skipcar+" que s'il n'y a vraiment AUCUN cofinancement au total
  if (!isLoading && !isTableLoading && !searchQuery && totalGlobalCofinancements === 0) {
    return (
      <Container maxWidth={false}>
        <Grid container justifyContent="center" alignItems="center" sx={{ minHeight: '200px' }}>
          <Paper
            elevation={5}
            sx={{
              padding: theme.spacing(2),
              display: 'flex',
              flexDirection: 'column',
              marginTop: theme.spacing(0),
              width: '100%',
              maxWidth: '500px',
              background: 'linear-gradient(135deg, #fff8e1 0%, #ffe57f 100%)',
            }}
          >
            <Typography
              variant="h6"
              align="center"
              sx={{
                marginBottom: theme.spacing(1),
                color: 'black',
                fontWeight: 'bold',
              }}
            >
              📊 Pas encore de dossier Skipcar+
            </Typography>
            <Typography
              variant="body2"
              align="center"
              sx={{
                marginBottom: theme.spacing(2),
                color: 'black',
              }}
            >
              Les demandes Skipcar+ apparaîtront ici une fois créées par les agences.
            </Typography>
          </Paper>
        </Grid>
      </Container>
    );
  }

  return (
    <Container maxWidth={false}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginTop: 5,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <TextField
            label="Rechercher"
            className="inputElement"
            variant="outlined"
            value={searchQuery}
            onChange={(e) => handleSearch(e.target.value)}
            sx={{ marginRight: 0 }}
          />
          <Button
            variant="contained"
            onClick={() => handleSearch(searchQuery)}
            className="dealActionButton"
            sx={{ ml: 1, fontWeight: 900 }}
          >
            Rechercher
          </Button>
          {hasSearched && (
            <Button
              className="secondaryDealActionButton"
              variant="contained"
              sx={{ ml: 1 }}
              color="error"
              onClick={() => resetSearch()}
            >
              Revenir à la liste principale
            </Button>
          )}
        </Box>
        <TablePagination
          className="custom-pagination"
          rowsPerPageOptions={[25, 50, 100]}
          component="div"
          count={totalItems || 0}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage={'Lignes par page'}
          labelDisplayedRows={({ from, to }) => `de ${from} à ${to}`}
        />
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginTop: 5,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            variant="contained"
            startIcon={<SyncIcon />}
            className="dealActionButton"
            sx={{ fontWeight: 800 }}
            onClick={() => onRefresh()}
            disabled={isTableLoading || isFullSyncLoading}
          >
            {isTableLoading && !isFullSyncLoading ? 'Rafraîchissement...' : 'Rafraichir / Mettre à jour'}
          </Button>
          <Button
            variant="outlined"
            startIcon={<SyncIcon />}
            className="secondaryDealActionButton"
            sx={{ fontWeight: 600 }}
            onClick={() => onRefresh(true)}
            disabled={isTableLoading || isFullSyncLoading}
            title="Synchronisation complète avec délais plus longs et invalidation totale du cache"
          >
            {isFullSyncLoading ? 'Sync complète...' : 'Sync complète'}
          </Button>
          {userRole === 'god' && (
            <Button variant="outlined" className="secondaryDealActionButton" onClick={() => navigate('/skipcar-plus/profils')}>
              Profils de taux
            </Button>
          )}
        </Box>
      </Box>

      <Paper sx={{ marginTop: 5, padding: 0 }} elevation={3}>
        {!hasSearched && (
          <Tabs
            value={selectedTab}
            className="DealsListTabs"
            onChange={handleTabChange}
            aria-label="Skipcar+ List Tabs"
          >
            <Tab label="Toutes" />
            <Tab label="En attente" />
            <Tab label="Validé par le client" />
            <Tab label="Validés" />
            <Tab label={<span style={{ color: 'red' }}>Supprimées</span>} />
          </Tabs>
        )}
        <Box
          sx={{
            textAlign: 'center',
            fontSize: 20,
            fontWeight: 600,
            background: '#979ED2',
            width: '100%',
            fontFamily: 'Urbanist',
            paddingTop: 2,
            paddingBottom: 2,
            color: 'white',
          }}
        >
          Dossiers Skipcar+
        </Box>
        <TableContainer>
          <Table stickyHeader>
            <TableHead className="stickyTableHead">
              <TableRow sx={{ background: '#262626' }}>
                <TableCell sx={{ ...stickyCellStyle, width: '30px', padding: 0, paddingLeft: 1 }}></TableCell>
                <TableCell sx={{ ...stickyCellStyle, width: '90px' }}>Plaque</TableCell>
                <TableCell sx={{ ...stickyCellStyle, cursor: 'pointer' }} onClick={() => handleSort('created_at')}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    Date
                    {sortColumn === 'created_at' && (
                      <SortIcon>{sortOrder === 'ASC' ? <ArrowUpwardIcon /> : <ArrowDownwardIcon />}</SortIcon>
                    )}
                  </Box>
                </TableCell>
                <TableCell
                  sx={{ ...stickyCellStyle, width: '90px', cursor: 'pointer' }}
                  onClick={() => handleSort('status')}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    État
                    {sortColumn === 'status' && (
                      <SortIcon>{sortOrder === 'ASC' ? <ArrowUpwardIcon /> : <ArrowDownwardIcon />}</SortIcon>
                    )}
                  </Box>
                </TableCell>
                <TableCell sx={{ ...stickyCellStyle }}>Agence</TableCell>
                <TableCell sx={{ ...stickyCellStyle }}>Marque / Modèle</TableCell>
                <TableCell sx={{ ...stickyCellStyle }}>Kilométrage</TableCell>
                <TableCell sx={{ ...stickyCellStyle }}>MEC</TableCell>
                <TableCell sx={{ ...stickyCellStyle, width: '100px' }}>Prix</TableCell>
                <TableCell sx={{ ...stickyCellStyle, minWidth: '130px', textAlign: 'center' }}>Infos</TableCell>
                <TableCell
                  sx={{ ...stickyCellStyle, width: '50px', cursor: 'pointer' }}
                  onClick={() => handleSort('id')}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    ID
                    {sortColumn === 'id' && (
                      <SortIcon>{sortOrder === 'ASC' ? <ArrowUpwardIcon /> : <ArrowDownwardIcon />}</SortIcon>
                    )}
                  </Box>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isTableLoading ? (
                <TableRow>
                  <TableCell colSpan={11} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : (
                cofinancementData.map((cofinancement: CofinancementData) => (
                  <TableRow key={cofinancement.id}>
                    <TableCell sx={{ width: '30px', padding: 0, paddingLeft: 1 }}>
                      {isDeleting ? (
                        <CircularProgress size={24} />
                      ) : (
                        <DeleteIcon
                          sx={{ cursor: 'pointer', color: 'red' }}
                          onClick={() => handleDeleteCofinancement(cofinancement.id)}
                        />
                      )}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{cofinancement.registration_plate || 'N/A'}</TableCell>
                    <TableCell>{formatDate(cofinancement.created_at)}</TableCell>
                    <TableCell>
                      <Typography
                        align="center"
                        sx={{
                          fontSize: '16px',
                          lineHeight: '1.25em',
                          ...getStatusStyle(cofinancement.status),
                        }}
                        variant="body2"
                      >
                        {getStatusLabel(cofinancement.status)}
                      </Typography>
                    </TableCell>
                    <TableCell>{cofinancement.org_name || 'N/A'}</TableCell>
                    <TableCell>
                      <Typography sx={{ fontSize: 15 }}>{cofinancement.make || 'N/A'}</Typography>
                      {cofinancement.model || 'N/A'}
                    </TableCell>
                    <TableCell>{formatKilometers(cofinancement.kms)}</TableCell>
                    <TableCell>{formatMEC(cofinancement.reg_date)}</TableCell>
                    <TableCell style={{ fontWeight: 600 }}>{formatPrice(cofinancement.purchase_price)}</TableCell>
                    <TableCell sx={{ textAlign: 'center' }}>
                      <Stack direction="column" spacing={1} alignItems="center">
                        <Button
                          sx={{ fontSize: 14, width: 152 }}
                          className="secondaryDealActionButton"
                          variant="contained"
                          onClick={() => handleCofinancementClick(cofinancement.deal_id)}
                        >
                          Voir la simulation
                        </Button>
                        {cofinancement.status === 'skipcar_validated' && (
                          <Button
                            sx={{ fontSize: 14, width: 152 }}
                            variant="outlined"
                            onClick={() => navigate(`/skipcar-plus/${cofinancement.deal_id}`)}
                          >
                            Contrat Skipcar+
                          </Button>
                        )}
                        <Typography variant="body2" sx={{ fontSize: '12px' }}>
                          Rotation: {cofinancement.estimated_rotation_weeks} semaines
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>{cofinancement.id}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            padding: 2,
          }}
        >
          <TablePagination
            className="custom-pagination"
            rowsPerPageOptions={[25, 50, 100]}
            component="div"
            count={totalItems}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage={'Lignes par page'}
            labelDisplayedRows={({ from, to }) => `de ${from} à ${to}`}
          />
        </Box>
      </Paper>
    </Container>
  );
};

export default CofinancementList;
